import { useEffect, useId, useMemo, useRef, useState } from "react";

// Spacing of the site's background dot grid. DotGridGlow lights up the same grid.
export const DOT_PITCH = 17;

const COLS = 39;
const ROWS = 21;
// Cells ([column, row]) drawn as brand-blue accents instead of faint gray.
const ACCENTS = new Set(
  [
    [11, 2], [14, 3], [9, 4], [19, 4], [25, 4], [7, 5], [18, 5], [22, 5], [27, 5], [4, 6],
    [12, 6], [24, 6], [32, 6], [6, 7], [16, 7], [22, 7], [33, 7], [37, 7], [6, 8], [18, 8],
    [26, 8], [0, 9], [10, 9], [16, 9], [27, 9], [31, 9], [0, 10], [12, 10], [20, 10], [33, 10],
    [4, 11], [10, 11], [21, 11], [25, 11], [33, 11], [6, 12], [14, 12], [27, 12], [37, 12], [4, 13],
    [15, 13], [19, 13], [27, 13], [0, 14], [8, 14], [21, 14], [31, 14], [37, 14], [9, 15], [13, 15],
    [21, 15], [33, 15],
  ].map(([c, r]) => r * COLS + c),
);

// Position for the first dot that puts it on the page-wide grid, as close as
// possible to where the patch was originally drawn (`start`).
function snapToGrid(start: number, pagePos: number) {
  const offset = (((-pagePos) % DOT_PITCH) + DOT_PITCH) % DOT_PITCH;
  return offset + DOT_PITCH * Math.round((start - offset) / DOT_PITCH);
}

// A patch of the site's faint background dot grid that fades out toward its
// edges. Its dots are snapped to one page-wide grid (origin at the page's top
// left), so every patch lines up with the others and with DotGridGlow's lit dots.
function DotEffect() {
  const ref = useRef<SVGSVGElement>(null);
  const id = useId().replace(/:/g, "");
  const [origin, setOrigin] = useState({ x: 22, y: 15 });

  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const align = () => {
      const rect = svg.getBoundingClientRect();
      const x = snapToGrid(22, rect.left + window.scrollX);
      const y = snapToGrid(15, rect.top + window.scrollY);
      setOrigin((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
    };
    align();
    // Layout above the patch can shift as fonts and images load.
    const observer = new ResizeObserver(align);
    observer.observe(document.body);
    window.addEventListener("resize", align);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", align);
    };
  }, []);

  const dots = useMemo(() => {
    const rects = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const accent = ACCENTS.has(r * COLS + c);
        rects.push(
          <rect
            key={r * COLS + c}
            x={origin.x + c * DOT_PITCH}
            y={origin.y + r * DOT_PITCH}
            width={1}
            height={1}
            fill={accent ? "#2589FF" : "#878794"}
            fillOpacity={accent ? undefined : "0.4"}
          />,
        );
      }
    }
    return rects;
  }, [origin]);

  return (
    <svg
      ref={ref}
      width={693}
      height={363}
      viewBox="0 0 693 363"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id={`${id}-mask`}
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x={0}
        y={0}
        width={693}
        height={363}
      >
        <rect width={693} height={363} fill={`url(#${id}-fade)`} />
      </mask>
      <g mask={`url(#${id}-mask)`}>{dots}</g>
      <defs>
        <radialGradient
          id={`${id}-fade`}
          cx={0}
          cy={0}
          r={1}
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(352 132.5) rotate(86.7948) scale(125.196 296.209)"
        >
          <stop stopColor="#D9D9D9" />
          <stop offset={1} stopColor="#737373" stopOpacity={0} />
        </radialGradient>
      </defs>
    </svg>
  );
}

export default DotEffect;
